import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef } from 'react';

import { CamerasApiKeys } from '../../api/cameras.api.js';
import { NvrsApiKeys, type NvrSyncStatus, useNvrsForPatient, useNvrSyncStatuses, useTriggerNvr } from '../../api/nvrs.api.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Nvr } from '../../models/index.js';
import NvrDelete from '../Nvr/NvrDelete.js';
import NvrForm from '../Nvr/NvrForm.js';
import SyncChip from '../Nvr/SyncChip.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

interface PatientNvrsTableProps {
  patientId?: string;
  title?: string;
}

const PatientNvrsTable: FC<PatientNvrsTableProps> = ({ patientId, title }) => {
  const { data = [], isLoading, refetch } = useNvrsForPatient(patientId);
  const nvrIds = useMemo(() => data.map((n) => n.id), [data]);
  const { data: syncStatuses = {}, refetch: refetchSync } = useNvrSyncStatuses(nvrIds);

  const { open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen } =
    useDialogControls<Nvr>(data);
  const { open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete } = useDialogControls<Nvr>(data);

  const triggerMutation = useTriggerNvr({ onSuccess: () => refetchSync() });
  const { canManageCamera, canDeleteCamera } = useCheckPermissions();

  const queryClient = useQueryClient();
  const prevStatuses = useRef<Record<string, NvrSyncStatus['status']>>({});
  useEffect(() => {
    const justSucceeded = Object.entries(syncStatuses).some(([id, s]) => {
      const prev = prevStatuses.current[id];
      return s.status === 'success' && prev && prev !== 'success';
    });
    prevStatuses.current = Object.fromEntries(
      Object.entries(syncStatuses).map(([id, s]) => [id, s.status]),
    );
    if (justSucceeded) {
      queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERAS]);
      queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA_FOR_PATIENT]);
      queryClient.invalidateQueries([NvrsApiKeys.GET_NVRS]);
      queryClient.invalidateQueries([NvrsApiKeys.GET_NVR]);
    }
  }, [syncStatuses, queryClient]);

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'ipAddress', headerName: 'IP Address', flex: 1 },
      { field: 'port', headerName: 'Port', flex: 1 },
      { field: 'cameras', headerName: 'Cameras', flex: 1 },
      {
        field: 'sync',
        headerName: 'Sync',
        width: 130,
        sortable: false,
        renderCell: (params) => <SyncChip status={syncStatuses[params.id as string]}/>,
      },
    ];
    if (canManageCamera || canDeleteCamera) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        type: 'actions',
        getActions: (params) => {
          const base = tableActions(onDelete(params.id), onFormOpen(params.id))({
            canEdit: canManageCamera,
            canDelete: canDeleteCamera,
          });
          return [
            ...base,
            (<GridActionsCellItem
              id="trigger"
              label="Rescan"
              onClick={() => triggerMutation.mutate(params.id as string)}
              icon={(
                <Tooltip arrow title={'Rescan NVR for cameras'}>
                  <RefreshIcon fontSize="small"/>
                </Tooltip>
              )}
            />),
          ];
        },
      });
    }
    return cols;
  }, [canManageCamera, canDeleteCamera, onFormOpen, onDelete, triggerMutation, syncStatuses]);

  const rows: GridRowsProp = useMemo(
    () => data.map((n) => ({ id: n.id, name: n.name, ipAddress: n.ipAddress, port: `ONVIF: ${n.onvifPort}, RTSP: ${n.rtspPort}, HTTP: ${n.httpPort ?? 'N/A'}`, cameras: (n.cameras || []).map((c) => c.name).join(', ') })),
    [data],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 1.5 }}>
        {title ? (
          <Typography variant={'h5'} component={'h3'}>
            {title}
          </Typography>
        ) : null}
        {canManageCamera ? (
          <Button
            variant={'contained'}
            size={'small'}
            startIcon={<AddIcon />}
            aria-label={'Add a new NVR'}
            onClick={() => setFormOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Add NVR
          </Button>
        ) : null}
      </Stack>
      <Table columns={columns} rows={rows} pageSize={10} NoRowsOverlayText={'No NVRs have been connected to this profile.'} isLoading={isLoading} />
      <NvrForm open={formOpen} onClose={resetForm} nvrId={formData?.id} patientId={patientId} refetch={refetch} />
      <NvrDelete open={deleteOpen} onClose={resetDelete} nvrId={deleteData?.id} refetch={refetch} />
    </Box>
  );
};

export default PatientNvrsTable;
